/**
 * Видимая часть блока модуля — чтобы сайдбар истории вставал по ней, а не по
 * всей высоте расчёта.
 *
 * Зачем. Сайдбар рендерится без портала (`getContainer={false}`), то есть
 * абсолютно позиционируется от блока модуля. Блок этот высотой в весь расчёт —
 * тысячи пикселей, — и шапка сайдбара с заголовком и крестиком оказывается
 * прибита к его верху. Стоит пользователю прокрутить расчёт и открыть историю,
 * как он видит середину панели: ни заголовка, ни кнопки закрытия (снимок с
 * платформы 2026-08-24; воспроизведено в харнессе — шапка на 679 px выше
 * видимой области).
 *
 * Прежняя страховка от этого — `scrollIntoView` по строке действий — не
 * работала по построению: строка липкая, она видна ВСЕГДА, и браузеру нечего
 * было доводить до экрана.
 *
 * Контейнер прокрутки заранее неизвестен: в standalone это окно, в embed —
 * модалка платформы, о внутреннем устройстве которой мы не вправе гадать.
 * Поэтому он находится по дереву, а не задаётся константой.
 */

import { useEffect, useState, type RefObject } from 'react';

export interface VisibleBox {
  /** Отступ сверху внутри блока — сколько его уже ушло за верхнюю кромку. */
  top: number;
  height: number;
}

interface Span {
  top: number;
  height: number;
}

/**
 * Пересечение блока с окном прокрутки, в координатах самого блока.
 *
 * Чистая функция: геометрию можно проверить тестом, не поднимая браузер.
 */
export function visibleBoxWithin(container: Span, block: Span): VisibleBox {
  const top = Math.max(0, container.top - block.top);
  const bottom = Math.min(block.height, container.top + container.height - block.top);
  return { top, height: Math.max(0, bottom - top) };
}

/** Ближайший предок, который реально прокручивается, либо `null` — значит окно. */
function findScrollParent(element: HTMLElement): HTMLElement | null {
  let current = element.parentElement;
  while (current !== null && current !== document.body && current !== document.documentElement) {
    const { overflowY } = getComputedStyle(current);
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') return current;
    current = current.parentElement;
  }
  return null;
}

/**
 * Видимая часть блока, в котором живёт `anchorRef`, пока `active`.
 *
 * `null`, пока измерять нечего (панель закрыта или разметки ещё нет) — вызывающий
 * код в этом случае не навязывает сайдбару никакой геометрии.
 */
export function useVisibleBox(
  anchorRef: RefObject<HTMLElement | null>,
  active: boolean,
): VisibleBox | null {
  const [box, setBox] = useState<VisibleBox | null>(null);

  useEffect(() => {
    if (!active) return;
    const anchor = anchorRef.current;
    // offsetParent — ближайший позиционированный предок, то есть ровно тот блок,
    // от которого считается `position: absolute` самого сайдбара.
    const block = anchor?.offsetParent;
    if (!(block instanceof HTMLElement)) return;

    const scroller = findScrollParent(block);

    const measure = () => {
      const blockRect = block.getBoundingClientRect();
      const containerSpan: Span = scroller
        ? { top: scroller.getBoundingClientRect().top, height: scroller.clientHeight }
        : { top: 0, height: window.innerHeight };
      setBox(visibleBoxWithin(containerSpan, { top: blockRect.top, height: blockRect.height }));
    };

    measure();
    const target: HTMLElement | Window = scroller ?? window;
    target.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    return () => {
      target.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, [active, anchorRef]);

  return box;
}
